import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Building2, Eye, Mail, MailCheck, MailPlus, Phone, Plus } from "lucide-react";
import { API_ADHOC_EMAIL_POC_LIST, API_V1_ADHOC_EMAIL_REQUESTS } from "../../../config/Endpoints.js";
import { formatCalendarDateDisplay } from "../../../utils/helpers.js";
import { Card } from "../../components/shared/card/card.tsx";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Badge, Button, Modal, RadioGroup, Select, TextField } from "../../components/ui/index.ts";
import { ResponsiveDataTable, type ResponsiveColumn } from "../../components/ui/responsive-table/responsive-table.tsx";
import { useCompanyNameOptions } from "../../hooks/use-company-name-options.ts";
import { getApi, postApi } from "../../utils/api.ts";
import { accessToken } from "../../utils/auth.ts";
import { showError, showSuccess } from "../../utils/toast.ts";
import { AdhocEmailFilters } from "./adhoc-email-filters.tsx";
import { companyNameLabel, employeeTypeLabel, parseAdhocList, proposedEmail, rowEmployeeName, type AdhocEmailRow } from "./adhoc-email.types.ts";
import styles from "./adhoc-email.module.css";

type FormState = {
  requestType: string; employeeType: string; firstName: string; lastName: string; personalEmail: string;
  proposedEmail: string; companyName: string; phoneNumber: string; agencyName: string;
  agencyContactNumber: string; pocId: string;
};
type Errors = Partial<Record<keyof FormState, string>>;
type Filters = { employeeName: string; status: string };

const EMPTY_FORM: FormState = { requestType: "", employeeType: "", firstName: "", lastName: "", personalEmail: "", proposedEmail: "", companyName: "", phoneNumber: "", agencyName: "", agencyContactNumber: "", pocId: "" };
const EMPTY_FILTERS: Filters = { employeeName: "", status: "PENDING" };
const REQUEST_TYPES = [
  { value: "EMAIL_CREATION", label: "Email Creation Only" },
  { value: "EMAIL_CREATION_WITH_TIMESHEET", label: "Email Creation + Access to Timesheet Module (CaeliusOne)" },
];
const EMPLOYEE_TYPES = [{ value: "REGULAR", label: "Regular" }, { value: "CONSULTANT", label: "Consultant" }];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COMPANY_EMAIL_DOMAINS = ["@caeliusconsulting.com", "@theivyclinics.com", "@sqelabs.com"];

function statusVariant(status?: string | null): "success" | "warning" | "danger" | "neutral" {
  const value = String(status ?? "").toUpperCase();
  if (value === "APPROVED") return "success";
  if (value === "REJECTED") return "danger";
  return value === "PENDING" ? "warning" : "neutral";
}

function validate(form: FormState): Errors {
  const errors: Errors = {};
  if (!form.requestType) errors.requestType = "Request type is required.";
  if (!form.employeeType) errors.employeeType = "Resource type is required.";
  if (!form.firstName.trim()) errors.firstName = "First name is required.";
  if (!form.lastName.trim()) errors.lastName = "Last name is required.";
  if (!EMAIL_PATTERN.test(form.personalEmail.trim())) errors.personalEmail = "Enter a valid personal email address.";
  const consultantTimesheet = form.requestType === "EMAIL_CREATION_WITH_TIMESHEET" && form.employeeType === "CONSULTANT";
  if (consultantTimesheet) {
    if (!form.companyName) errors.companyName = "Company name is required.";
    if (!EMAIL_PATTERN.test(form.proposedEmail.trim())) errors.proposedEmail = "Enter a valid proposed email address.";
    else if (!COMPANY_EMAIL_DOMAINS.some((domain) => form.proposedEmail.trim().toLowerCase().endsWith(domain))) errors.proposedEmail = "Use a Caelius Consulting, Ivy Clinics, or SQE Labs email address.";
    if (!/^\d{10}$/.test(form.phoneNumber)) errors.phoneNumber = "Contact number must contain 10 digits.";
    if (form.agencyContactNumber && !/^\d{10}$/.test(form.agencyContactNumber)) errors.agencyContactNumber = "Agency contact number must contain 10 digits.";
    if (!form.pocId) errors.pocId = "Point of contact is required.";
    return errors;
  }
  if (!EMAIL_PATTERN.test(form.proposedEmail.trim())) errors.proposedEmail = "Enter a valid proposed email address.";
  else if (!COMPANY_EMAIL_DOMAINS.some((domain) => form.proposedEmail.trim().toLowerCase().endsWith(domain))) errors.proposedEmail = "Use a Caelius Consulting, Ivy Clinics, or SQE Labs email address.";
  if (!form.companyName) errors.companyName = "Company name is required.";
  if (!/^\d{10}$/.test(form.phoneNumber)) errors.phoneNumber = "Contact number must contain 10 digits.";
  if (form.agencyContactNumber && !/^\d{10}$/.test(form.agencyContactNumber)) errors.agencyContactNumber = "Agency contact number must contain 10 digits.";
  return errors;
}

export default function RaiseEmailRequestPage() {
  const companies = useCompanyNameOptions();
  const [rows, setRows] = useState<AdhocEmailRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState<Filters>(EMPTY_FILTERS);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewing, setViewing] = useState<AdhocEmailRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Errors>({});
  const [pocOptions, setPocOptions] = useState<{ value: string; label: string }[]>([]);
  const [pocLoading, setPocLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        page: String(page - 1),
        size: String(pageSize),
        status: filters.status.toUpperCase(),
      });
      if (filters.employeeName.trim()) query.set("employeeName", filters.employeeName.trim());
      const { data } = await getApi<unknown>(`${API_V1_ADHOC_EMAIL_REQUESTS}?${query.toString()}`, accessToken());
      const result = parseAdhocList(data);
      setRows(result.rows); setTotal(result.total);
    } catch (error) { showError(error instanceof Error ? error.message : "Unable to load email requests."); setRows([]); setTotal(0); }
    finally { setLoading(false); }
  }, [filters.employeeName, filters.status, page, pageSize]);
  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!modalOpen) return;
    const controller = new AbortController();
    const loadPocs = async () => {
      setPocLoading(true);
      try {
        const { data } = await getApi<unknown>(API_ADHOC_EMAIL_POC_LIST, accessToken(), "json", controller.signal);
        const root = data && typeof data === "object" ? data as Record<string, unknown> : {};
        const nested = root.data && typeof root.data === "object" ? root.data as Record<string, unknown> : {};
        const list = Array.isArray(nested.data) ? nested.data : [];
        setPocOptions(list.flatMap((item) => {
          if (!item || typeof item !== "object") return [];
          const poc = item as Record<string, unknown>;
          return poc.id != null && poc.name ? [{ value: String(poc.id), label: String(poc.name) }] : [];
        }));
      } catch (error) {
        if (!controller.signal.aborted) {
          setPocOptions([]);
          showError(error instanceof Error ? error.message : "Unable to load POC list.");
        }
      } finally {
        if (!controller.signal.aborted) setPocLoading(false);
      }
    };
    void loadPocs();
    return () => controller.abort();
  }, [modalOpen]);

  const displayedRows = rows;
  const companyOptions = useMemo(() => companies.options
    .filter((option) => ["CCI_INDIA", "CCI_US"].includes(String(option.value).toUpperCase())), [companies.options]);
  const isUsCompany = form.companyName === "CCI_US";
  const isTimesheetRequest = form.requestType === "EMAIL_CREATION_WITH_TIMESHEET";
  const isConsultantTimesheet = isTimesheetRequest && form.employeeType === "CONSULTANT";
  const blockedRegularTimesheet = isTimesheetRequest && form.employeeType === "REGULAR";
  const phoneCountryCode = isUsCompany ? "+1" : "+91";
  const update = (key: keyof FormState, value: string) => { setForm((current) => ({ ...current, [key]: value, ...(key === "companyName" && value !== "CCI_US" ? { agencyName: "", agencyContactNumber: "" } : {}) })); setErrors((current) => ({ ...current, [key]: "" })); };
  const closeModal = () => { if (saving) return; setModalOpen(false); setForm(EMPTY_FORM); setErrors({}); };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const nextErrors = validate(form); setErrors(nextErrors);
    if (Object.keys(nextErrors).length || blockedRegularTimesheet) return;
    setSaving(true);
    try {
      const payload = isConsultantTimesheet ? {
        pocId: form.pocId,
        empType: form.employeeType,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        companyName: form.companyName,
        personalEmail: form.personalEmail.trim(),
        proposedEmail: form.proposedEmail.trim(),
        contactNo: form.phoneNumber,
        agencyName: form.agencyName.trim() || null,
        agencyContactNumber: form.agencyContactNumber || null,
      } : {
        empType: form.employeeType,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        personalEmail: form.personalEmail.trim(),
        proposedEmail: form.proposedEmail.trim(),
        contactNo: form.phoneNumber,
        agencyName: form.agencyName.trim() || null, agencyContactNumber: form.agencyContactNumber || null,
        companyName: form.companyName,
      };
      const { data } = await postApi<Record<string, unknown>>(API_V1_ADHOC_EMAIL_REQUESTS, accessToken(), payload);
      showSuccess(String(data?.message ?? "Email request created successfully."));
      setModalOpen(false); setForm(EMPTY_FORM); setErrors({}); setPage(1); await load();
    } catch (error) { showError(error instanceof Error ? error.message : "Unable to create email request."); }
    finally { setSaving(false); }
  };

const columns: ResponsiveColumn<AdhocEmailRow>[] = [
  {
    id: "employee",
    header: "Name",
    sortable: true,
    getSortValue: rowEmployeeName,
    render: (row) => rowEmployeeName(row),
    truncate: true,
    getTitleText: rowEmployeeName,
  },
  {
    id: "companyName",
    header: "Company Name",
    render: (row) => companyNameLabel(row.companyName) || "-",
    truncate: true,
  },
  {
    id: "personalEmail",
    header: "Personal Email",
    accessor: "personalEmail",
    truncate: true,
  },
  // {
  //   id: "proposedEmail",
  //   header: "Proposed Email",
  //   sortable: true,
  //   getSortValue: proposedEmail,
  //   render: (row) => proposedEmail(row),
  //   truncate: true,
  //   getTitleText: proposedEmail,
  // },
 {
  id: "requestedAt",
  header: "Requested On",
  accessor: "createdAt",
  sortable: true,
  render: (row) => formatCalendarDateDisplay(row.createdAt),
  nowrap: true,
},
 {
  id: "status",
  header: "Status",
  accessor: "status",
  render: (row) => {
    const statusLabel =
      row.status
        ?.toLowerCase()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase()) || "-";

    return (
      <Badge
        className={styles.status}
        variant={statusVariant(row.status)}
      >
        {statusLabel}
      </Badge>
    );
  },
  nowrap: true,
},
  {
    id: "action",
    header: "Action",
    render: (row) => (
      <Button
        size="sm"
        variant="muted-outline"
        startIcon={<Eye size={15} />}
        onClick={() => setViewing(row)}
      >
        View
      </Button>
    ),
    nowrap: true,
  },
];
  const viewDetails: Array<{ label: string; value: unknown; icon: ReactNode }> = viewing ? [
    ...(viewing.status?.trim().toUpperCase() !== "CONFIGURED" ? [
      { label: "First Name", value: viewing.firstName?.trim() || "Not available", icon: <Building2 size={18} /> },
      { label: "Last Name", value: viewing.lastName?.trim() || "Not available", icon: <Building2 size={18} /> },
    ] : []),
    { label: "Resource Type", value: viewing.empType ?? viewing.employeeType ? employeeTypeLabel(viewing.empType ?? viewing.employeeType) : null, icon: <Building2 size={18} /> },
    { label: "Personal Email", value: viewing.personalEmail, icon: <Mail size={18} /> },
    ...(viewing.status?.trim().toUpperCase() === "CONFIGURED" ? [{
      label: "Configured Email",
      value: viewing.ccEmail?.trim() || "Not available",
      icon: <MailCheck size={18} />,
    }] : []),
    { label: "Proposed Email", value: proposedEmail(viewing) === "-" ? null : proposedEmail(viewing), icon: <Mail size={18} /> },
    { label: "Employee Contact Number", value: viewing.contactNumber ?? viewing.contactNo ?? viewing.phoneNumber, icon: <Phone size={18} /> },
    { label: "Company Name", value: companyNameLabel(viewing.companyName), icon: <Building2 size={18} /> },
    { label: "Agency Name", value: viewing.agencyName, icon: <Building2 size={18} /> },
    { label: "Agency Contact Number", value: viewing.agencyContactNumber, icon: <Phone size={18} /> },
    // { label: "Created By", value: viewing.createdBy, icon: <UserRound size={18} /> },
  ].filter((item) => item.value != null && item.value !== "") : [];


  return <PageShell><div className={styles.page}>
    <Card className={styles.filterCard}><AdhocEmailFilters employeeName={draftFilters.employeeName} status={draftFilters.status} busy={loading} onEmployeeChange={(employeeName) => setDraftFilters((current) => ({ ...current, employeeName }))} onStatusChange={(status) => setDraftFilters((current) => ({ ...current, status }))} onApply={() => { setFilters(draftFilters); setPage(1); }} onClear={() => { setDraftFilters(EMPTY_FILTERS); setFilters(EMPTY_FILTERS); setPage(1); }} trailingActions={<><Button variant="accent" startIcon={<Plus size={16} />} onClick={() => setModalOpen(true)}>Add Request</Button></>} /></Card>
    <section className={styles.tablePanel}><ResponsiveDataTable columns={columns} data={displayedRows} serialNumber page={page} pageSize={pageSize} totalItems={total} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} totalLabel={`Total Records: ${total}`} aria-label="Adhoc email requests" emptyState={<div className={styles.empty}>{loading ? "Loading requests..." : "No email requests available"}</div>} /></section>
    <Modal open={modalOpen} onClose={closeModal} title="Add Email Request" size="lg" footer={<div className={styles.modalFooter}><Button variant="light-grey" disabled={saving} onClick={closeModal}>Cancel</Button><Button type="submit" form="adhoc-email-request-form" startIcon={isConsultantTimesheet ? <MailCheck size={16} /> : <MailPlus size={16} />} disabled={saving || blockedRegularTimesheet}>{saving ? "Submitting..." : isConsultantTimesheet ? "Notify POC" : "Submit Request"}</Button></div>}>
      <form id="adhoc-email-request-form" className={styles.modalForm} onSubmit={(event) => void submit(event)} noValidate><div className={styles.formGrid}>
        <div className={styles.fullWidth}><Select label="Request Type" required value={form.requestType} options={REQUEST_TYPES} placeholder="Select Request Type" error={errors.requestType} onChange={(value) => update("requestType", value)} /></div>
        <div className={styles.fullWidth}><RadioGroup name="employeeType" label="Resource Type" required options={EMPLOYEE_TYPES} value={form.employeeType} error={errors.employeeType} onChange={(value) => update("employeeType", value)} /></div>
        {blockedRegularTimesheet ? <p className={`${styles.notice} ${styles.fullWidth}`}>Regular Employees are already onboarded through HR and have access to CaeliusOne. Only Adhoc Email Creation can be requested for Regular Employees.</p> : <>
        <TextField
          id="adhoc-firstName"
          label="First Name"
          required
          maxLength={100}
          value={form.firstName}
          error={errors.firstName}
          placeholder="Enter First Name"
          onChange={(event) => update("firstName", event.target.value)}
        />
        <TextField
          id="adhoc-lastName"
          label="Last Name"
          required
          maxLength={100}
          value={form.lastName}
          error={errors.lastName}
          placeholder="Enter Last Name"
          onChange={(event) => update("lastName", event.target.value)}
        />
        {isConsultantTimesheet ? <><TextField id="adhoc-personal-email" type="email" label="Personal Email" required maxLength={100} value={form.personalEmail} error={errors.personalEmail} placeholder="name@example.com" onChange={(event) => update("personalEmail", event.target.value)} /><Select label="Company Name" required value={form.companyName} options={companyOptions} placeholder={companies.loading ? "Loading companies..." : "Select Company Name"} disabled={companies.loading} error={errors.companyName} onChange={(value) => update("companyName", value)} /><TextField id="adhoc-proposed-email" type="email" label="Proposed Email" required maxLength={100} value={form.proposedEmail} error={errors.proposedEmail} placeholder="name@caeliusconsulting.com" onChange={(event) => update("proposedEmail", event.target.value)} /><TextField id="adhoc-contact" className={styles.phoneInput} type="tel" inputMode="numeric" label="Employee Contact Number" required startIcon={<span className={styles.countryCode}>{phoneCountryCode}</span>} maxLength={10} value={form.phoneNumber} error={errors.phoneNumber} placeholder="9876543210" onChange={(event) => update("phoneNumber", event.target.value.replace(/\D/g, "").slice(0, 10))} />{isUsCompany ? <><TextField id="adhoc-agency-name" label="Agency Name" maxLength={100} value={form.agencyName} placeholder="Enter Agency Name" onChange={(event) => update("agencyName", event.target.value)} /><TextField id="adhoc-agency-contact" type="tel" inputMode="numeric" label="Agency Contact Number" maxLength={10} value={form.agencyContactNumber} error={errors.agencyContactNumber} placeholder="9876543210" onChange={(event) => update("agencyContactNumber", event.target.value.replace(/\D/g, "").slice(0, 10))} /></> : null}<div className={styles.fullWidth}><Select label="Point of Contact (POC)" required value={form.pocId} options={pocOptions} placeholder={pocLoading ? "Loading POCs..." : "Select POC"} disabled={pocLoading} error={errors.pocId} onChange={(value) => update("pocId", value)} /></div></> : <>
          <Select label="Company Name" required value={form.companyName} options={companyOptions} placeholder={companies.loading ? "Loading companies..." : "Select Company Name"} disabled={companies.loading} error={errors.companyName} onChange={(value) => update("companyName", value)} />
          {!isTimesheetRequest ? <>
          <TextField id="adhoc-personal-email" type="email" label="Personal Email" required maxLength={100} value={form.personalEmail} error={errors.personalEmail} placeholder="name@example.com" onChange={(event) => update("personalEmail", event.target.value)} />
          <TextField id="adhoc-proposed-email" type="email" label="Proposed Email" required maxLength={100} value={form.proposedEmail} error={errors.proposedEmail} placeholder="name@caeliusconsulting.com" onChange={(event) => update("proposedEmail", event.target.value)} />
          <TextField id="adhoc-contact" className={styles.phoneInput} type="tel" inputMode="numeric" label="Employee Contact No." required startIcon={<span className={styles.countryCode}>{phoneCountryCode}</span>} maxLength={10} value={form.phoneNumber} error={errors.phoneNumber} placeholder="9876543210" onChange={(event) => update("phoneNumber", event.target.value.replace(/\D/g, "").slice(0, 10))} />
          {isUsCompany && form.employeeType !== "CONSULTANT" ? <><TextField id="adhoc-agency-name" label="Agency Name" maxLength={100} value={form.agencyName} placeholder="Enter Agency Name" onChange={(event) => update("agencyName", event.target.value)} /><TextField id="adhoc-agency-contact" type="tel" inputMode="numeric" label="Agency Contact Number" maxLength={10} value={form.agencyContactNumber} error={errors.agencyContactNumber} placeholder="9876543210" onChange={(event) => update("agencyContactNumber", event.target.value.replace(/\D/g, "").slice(0, 10))} /></> : null}
          </> : null}
        </>}
        </>}
      </div></form>
    </Modal>
    <Modal open={viewing != null} onClose={() => setViewing(null)} title="Email Request Details" size="lg" footer={<Button variant="light-grey" onClick={() => setViewing(null)}>Close</Button>}>
      {viewing ? <section className={styles.requestView}>
        <header className={styles.requestViewHeader}>
          <div className={styles.requestIdentity}>
            <span className="body-sm">Name</span>
            <h3>{rowEmployeeName(viewing)}</h3>
          </div>
          {viewing.status ? <Badge className={styles.requestStatus} variant={statusVariant(viewing.status)}>{viewing.status.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())}</Badge> : null}
        </header>
        {viewDetails.length ? <dl className={styles.requestMeta}>{viewDetails.map((item) => <div className={styles.requestMetaItem} key={item.label}><span>{item.icon}</span><div><dt className="body-sm">{item.label}</dt><dd className="sub-heading-md"> {String(item.value)}</dd></div></div>)}</dl> : null}
      </section> : null}
    </Modal>
  </div></PageShell>;
}
